import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  alpha,
  Stack,
  Grow,
} from '@mui/material';
import { Mic, Square, Copy } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  ProgressModal,
  FeedbackSnackbar,
  uploadToScreenApp,
  UploadState,
  CopyState,
  useIframeResizer,
} from './common';

interface LiveTranscriberState extends UploadState, CopyState {
  isRecording: boolean;
  transcription: string;
}

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

const LiveTranscriber: React.FC = () => {
  useIframeResizer();

  const theme = useTheme();
  const { t } = useTranslation();
  const [state, setState] = useState<LiveTranscriberState>({
    isRecording: false,
    transcription: '',
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    copySuccess: false,
  });

  const recognition = useRef<any>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      mediaRecorder.current.stop();
      recognition.current?.stop();
      setState((prev) => ({ ...prev, isRecording: false }));

      mediaRecorder.current.stream.getTracks().forEach((track) => track.stop());
    }
  };

  const startRecording = async () => {
    try {
      setState((prev) => ({ ...prev, error: null, transcription: '' }));

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        uploadToScreenApp(
          audioBlob,
          'transcription.webm',
          () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
          (progress) => setState((prev) => ({ ...prev, progress })),
          () => setState((prev) => ({ ...prev, uploadComplete: true })),
          (error) => setState((prev) => ({ ...prev, error, uploading: false }))
        );
      };

      mediaRecorder.current.start();
      recognition.current?.start();
      setState((prev) => ({ ...prev, isRecording: true }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Error accessing microphone. Please check your permissions.',
      }));
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(state.transcription);
      setState((prev) => ({ ...prev, copySuccess: true }));
      setTimeout(
        () => setState((prev) => ({ ...prev, copySuccess: false })),
        2000
      );
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to copy to clipboard. Please try again.',
      }));
    }
  };

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.lang = 'en-US';

      recognition.current.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        setState((prev) => ({ ...prev, transcription: transcript }));
      };

      recognition.current.onerror = (event: any) => {
        setState((prev) => ({
          ...prev,
          error: `Error occurred in recognition: ${event.error}`,
          isRecording: false,
        }));
        stopRecording();
      };

      recognition.current.onend = () => {
        if (state.isRecording) {
          stopRecording();
        }
      };
    }

    return () => {
      if (recognition.current) {
        recognition.current.stop();
      }
    };
  }, [state.isRecording]);

  // Check for browser compatibility
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {t('liveTranscriber.messages.error')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      {state.error?.includes('microphone') && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: 'italic' }}
        >
          {t('liveTranscriber.messages.microphoneAccess')}
        </Typography>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mb: 3, minHeight: 56 }}
      >
        {!state.isRecording && !state.uploading && (
          <Button
            variant="contained"
            color="error"
            onClick={startRecording}
            startIcon={<Mic />}
            size="large"
            sx={{
              minWidth: 140,
              px: 3,
              borderRadius: 28,
              py: 2,
              fontSize: '1.2rem',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: '#d32f2f',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            {t('liveTranscriber.controls.start')}
          </Button>
        )}

        {state.isRecording && (
          <>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="contained"
                color="error"
                onClick={stopRecording}
                startIcon={<Square />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.2rem',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  },
                  animation: 'pulseRed 2s infinite',
                  '@keyframes pulseRed': {
                    '0%, 100%': {
                      backgroundColor: theme.palette.error.main,
                    },
                    '50%': {
                      backgroundColor: theme.palette.error.dark,
                    },
                  },
                }}
              >
                {t('liveTranscriber.controls.stop')}
              </Button>
            </Grow>
          </>
        )}

        {state.transcription && !state.isRecording && (
          <Grow in={!!state.transcription} timeout={300}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={copyToClipboard}
              startIcon={<Copy />}
              size="large"
              sx={{
                minWidth: 140,
                px: 3,
                borderRadius: 28,
                py: 2,
                fontSize: '1.2rem',
                '& .MuiSvgIcon-root': {
                  color: 'secondary.main',
                },
                '&:hover': {
                  '& .MuiSvgIcon-root': {
                    color: 'secondary.main',
                  },
                },
              }}
            >
              {t('liveTranscriber.controls.copy')}
            </Button>
          </Grow>
        )}
      </Stack>

      {state.isRecording && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 2.5,
            color: theme.palette.text.secondary,
            py: 2,
            px: 4,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 28,
              padding: '2px',
              background: 'linear-gradient(45deg, #e0e0e0, #9e9e9e)',
              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              borderRadius: 30,
              background: 'linear-gradient(45deg, #e0e0e0, #9e9e9e)',
              opacity: 0.1,
              filter: 'blur(8px)',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            },
            '@keyframes pulse': {
              '0%, 100%': {
                opacity: 0.1,
              },
              '50%': {
                opacity: 0.3,
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress
                size={16}
                sx={{
                  color: 'inherit',
                  position: 'absolute',
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                  animation: 'blink 1s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 100%': {
                      opacity: 0.2,
                    },
                    '50%': {
                      opacity: 0.8,
                    },
                  },
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                background: 'linear-gradient(45deg, #424242, #757575)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('liveTranscriber.messages.listening')}
            </Typography>
          </Box>
        </Box>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          minHeight: 200,
          maxHeight: 400,
          overflow: 'auto',
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          textAlign: 'left',
          fontFamily: theme.typography.fontFamily,
          position: 'relative',
          borderRadius: 2,
          borderColor: theme.palette.divider,
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
          ...(state.isRecording && {
            borderColor: theme.palette.error.main,
            boxShadow: `0 0 0 1px ${alpha(theme.palette.error.main, 0.2)}`,
          }),
        }}
      >
        <Typography
          variant="body1"
          component="div"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: theme.palette.text.primary,
            lineHeight: 1.8,
            fontSize: '1.1rem',
            animation: 'fadeIn 0.3s ease-out',
          }}
        >
          {state.transcription || (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                fontSize: '1rem',
                textAlign: 'center',
                mt: 4,
              }}
            >
              {state.isRecording
                ? t('liveTranscriber.messages.listening')
                : t('liveTranscriber.messages.notListening')}
            </Typography>
          )}
        </Typography>
      </Paper>

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('liveTranscriber.messages.processing')}
        completeMessage={t('liveTranscriber.messages.complete')}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('liveTranscriber.messages.error')}
        severity="error"
        autoHideDuration={6000}
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />

      <FeedbackSnackbar
        open={state.copySuccess}
        message={t('liveTranscriber.messages.copied')}
        severity="success"
        autoHideDuration={2000}
        onClose={() => setState((prev) => ({ ...prev, copySuccess: false }))}
      />
    </Box>
  );
};

export default LiveTranscriber;
